import {exam_getExaminationInfoList_Api, exam_getExaminationReportOnTopResult_Api, examinationSchema,} from 'apis'
import EmptyHolder from 'components/EmptyHolder'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {IoCall, IoMailOutline} from 'react-icons/io5'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper from 'views/AppLocals/hooks/usePaginationHelper'
import ItemTooltip from 'views/AppLocals/Templates/TooltipItem'
import {NullAsDefault, renderElse} from 'views/Shared'
import {renderReportRow, renderReportTable} from './renderReportTable'
import {useRouteSearchParam} from "../../../envs/_current/history";
import ExaminationRankingReportFilter from "./ExaminationRankingReportFilter";
import {renderIfElse} from "../../Shared";
import Translate from "../../../modules/local/Translate";
import {formatDate} from "../../../helpers/datetimeHelpers";
import dayjs from "dayjs";

const headers = ['full name', 'completion time', 'total point']

const schemas = [
    {
        name: 'name',
        value_path: 'examinee_name',
        value2_path: 'examinee_name',
        className: 'flex text-left font-medium text-color-000',
        className2: 'flex',
        wrapperClassName: 'flex flex-col',
        render2: (value, data) => {
            const phone = _.get(data, 'phone_number')
            const email = _.get(data, 'email')
            return (
                <div className="flex items-center gap-3 ml-1">
                    {!!phone && (
                        <ItemTooltip
                            icon={
                                <IoCall
                                    size={11}
                                    className="text-color-200"
                                />
                            }
                            value={phone}
                        />
                    )}
                    {!!email && (
                        <ItemTooltip
                            icon={
                                <IoMailOutline
                                    size={11}
                                    className="text-color-200"
                                />
                            }
                            value={email}
                        />
                    )}
                </div>
            )
        },
    },
    {
        name: 'time',
        value_path: 'working_time_in_seconds',
        value2_path: 'started_at',
        className: 'text-color-100',
        className2: 'flex items-baseline',
        wrapperClassName: 'flex items-baseline justify-center',
        render: (value, data) => {
            if (isNaN(value)) return value
            return (
                <span className="font-semibold text-sm text-color-100">
                    {dayjs.unix(value).utc().format('HH:mm:ss')}
                </span>
            )
        },
        render2: (value, data) => {
            return (
                <span className="text-2xs lowercase italic">
                    {dayjs(_.get(data, 'started_at')).fromNow()}
                </span>
            )
        },
    },
    {
        name: 'score',
        value_path: 'final_score',
        value2_path: 'final_score',
        className: 'font-semibold',
        className2: 'flex items-baseline',
        wrapperClassName: 'flex items-baseline justify-center',
        render: (value, data) => {
            return (
                <Translate>
                    {t => renderIfElse(
                        value,
                        <span className="text-primary">{`${value} %`}</span>,
                        <span className="font-normal text-xs text-color-400 italic capitalize">{t('not update')}</span>
                    )}
                </Translate>
            )
        },
        render2: (value, data) => {
            return (
                <span className="text-2xs lowercase italic">
                    {`${_.get(data, 'total_corrects')}/${_.get(data, 'total_questions')}`}
                </span>
            )
        },
    },
]

const ExaminationInfo = ({id}) => {
    const t = useTranslate()

    const item = useSelectEntities(id, examinationSchema, {})

    const {time_open, time_close, total_examination_paper} = item || {}

    const ItemInformation = ({label, value}) => (
        <div className="flex flex-col items-start">
      <span className="font-light text-xs text-color-400 italic">
        {t(label)}
          {' : '}
      </span>
            <span className="font-semibold text-xs text-color-000 leading-tight">
        {value}
      </span>
        </div>
    )

    return renderElse(
        _.isEmpty(item),
        <div className="flex flex-col gap-4 mb-10 mt-4">
            <div className="text-center text-xl font-bold text-primary">
                {getTitle(item)}
            </div>
            <div className="grid grid-cols-3 gap-2 px-8">
                <ItemInformation
                    label="start"
                    value={formatDate(time_open,'HH:mm - DD/MM/YYYY')}
                />
                <ItemInformation
                    label="end"
                    value={formatDate(time_close,'HH:mm - DD/MM/YYYY')}
                />
                <ItemInformation
                    label="total exam paper"
                    value={total_examination_paper}
                />
            </div>
        </div>
    )
}

const ExaminationRankingReport = ({
                                      owner_id,
                                      owner_type,
                                      Wrapper = 'div',
                                      filter_actions = {
                                          sortBy: 'sort-by',
                                          keyword: 'keyword',
                                          examination_id: 'examination',
                                          examination_info_ids: 'info',
                                          filter_examination_info_id: 'filter_info',
                                          examination_info_answer_id: 'answer',
                                      },
                                  }) => {
    const location = useLocation()

    const t = useTranslate()

    // const [examination_id, set_examination_id] = useState(filter_actions.examination_id)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const examination_id = useRouteSearchParam(filter_actions.examination_id)
    const filter_examination_info_id = useRouteSearchParam(
        filter_actions.filter_examination_info_id
    )
    const examination_info_answer_id = useRouteSearchParam(
        filter_actions.examination_info_answer_id
    )
    const examination_info_ids_param =
        useRouteSearchParam(filter_actions.examination_info_ids) || ''
    const examination_info_ids = examination_info_ids_param
        .split(',')
        .filter((e) => !!e)

    const apiInfo = exam_getExaminationInfoList_Api

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [
            apiInfo.path.replace(':id', examination_id),
            JSON.stringify({}),
        ].join('/'),
        apiInfo,
        values: {},
        // deps: [{}],
        query: bindQueryParam({id: examination_id}),
    })

    const selectedFields = _.isEmpty(examination_info_ids)
        ? _.get(response, 'data.data', [])
        : _.get(response, 'data.data', []).filter((e) =>
            examination_info_ids.includes(e.id)
        )

    const customHeaders = [...selectedFields.map((e) => e.label), ...headers]
    const customSchemas = [
        ...selectedFields.map((e) => {
            return {
                name: e.id,
                value_path: e.id,
                className: 'flex text-left font-medium text-color-000',
            }
        }),
        ...schemas,
    ]

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            examination_id,
            customHeaders.join('-'),
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, examination_id, customHeaders, location.state]
    )

    const [pager_widget, {widget_header}] = usePaginationHelper({
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange(sortBy)
            onFilterChange({
                examination_id,
                examination_info_ids,
                filter_examination_info_id,
                examination_info_answer_id,
            })
        },
        pagerInfo: {
            query: bindQueryParam({
                id: examination_id,
            }),
            PaginationMeta: NullAsDefault,
            apiInfo: exam_getExaminationReportOnTopResult_Api,
            renderWrap: renderReportTable(customHeaders),
            renderEmpty: () => (
                <tr>
                    <td
                        className="border p-2"
                        colSpan={customHeaders.length}>
                        <EmptyHolder/>
                    </td>
                </tr>
            ),
            renderItem: renderReportRow({
                fields: customSchemas,
            }),
        },
        withKey: ({sortBy, keyword, filterBy}) =>
            [
                ...refresh_deps,
                sortBy,
                keyword,
                selectedFields,
                JSON.stringify(filterBy),
            ].join('/'),
        dependencies: [

        ],
        filter_params: {
            owner_id,
            owner_type,
            examination_id,
            examination_info_ids,
            filter_examination_info_id,
            examination_info_answer_id,
        },
        SortByWidget: NullAsDefault,
        renderSearchBar: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        // renderFilter: NullAsDefault,
        AdvancedFilter: ExaminationRankingReportFilter,
    })

    return (
        <Wrapper className="space-y-3">
            <div className="font-semibold text-primary tracking-wide uppercase">
                {t('report')}
            </div>

            <div className="border rounded-lg p-3">
                <ExaminationInfo id={examination_id}/>
                <div className="flex flex-col">
                    <div className="text-center font-bold text-lg text-color-000 uppercase tracking-widest mb-4">
                        {t('exam ranking report')}
                    </div>
                    {examination_id ? pager_widget : widget_header}
                </div>
            </div>
        </Wrapper>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(ExaminationRankingReport)
